import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import {
  filter,
  map,
  Observable,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  ApiService,
  Currency,
  HostelResp,
  Room,
  Rooms,
} from '@app/services/api/api.service';
import { BedSelection, BookingService } from '@app/hostel/booking.service';
import { HostelSearchParams, SearchService } from '@app/search.service';
import { Router } from '@angular/router';
import { LocaleService } from '@app/locale.service';

@Component({
  selector: 'app-booking-widget',
  templateUrl: './booking-widget.component.html',
  styleUrls: ['./booking-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingWidgetComponent {
  @Input()
  public hostel: HostelResp;

  public selectedBeds: any[] = [];
  public total;
  public payableNow;
  public numNights = 1;
  public currency: Observable<Currency>;
  public locking = false;

  private destroyed = new Subject<void>();

  constructor(
    private readonly bookingService: BookingService,
    private readonly searchService: SearchService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly apiService: ApiService,
    private localeService: LocaleService
  ) {}

  ngOnInit() {
    this.currency = this.localeService.currentCurrency;

    this.searchService.searchParams$
      .pipe(takeUntil(this.destroyed))
      .subscribe((searchParams) => {
        this.numNights = this.searchService.calculateNights(
          searchParams['checkInDate'],
          searchParams['checkOutDate']
        );
      });

    this.bookingService.selectedBeds$
      .pipe(takeUntil(this.destroyed))
      .subscribe((beds) => {
        this.groupSelectedBeds(beds);
      });
  }

  private groupSelectedBeds(selectedBeds): void {
    this.selectedBeds = [];
    // THIS selected beds needs to be mapped over to get the rates
    selectedBeds.forEach((s: BedSelection, id: string) => {
      const rates = Object.keys(s.rates)
        .map((rateType) => ({
          rateType,
          count: s.rates[rateType] || 0,
        }))
        .filter((rate) => rate.count > 0);

      if (rates.length > 0) {
        this.selectedBeds.push({
          room: s.room,
          rates,
        });
      }
    });

    let totalPrice = 0;

    this.selectedBeds.forEach((s: any) => {
      const totalRoomPrice = s.rates.reduce((total, rate) => {
        return total + s.room.rates[rate.rateType].price * rate.count;
      }, 0);
      totalPrice += totalRoomPrice;
    });
    // need to be able to book multiple rooms
    this.total = totalPrice * this.numNights;
    this.payableNow = (this.total * 0.11).toFixed(2);

    this.cdr.markForCheck();
  }

  public lockBooking() {
    if (!this.selectedBeds.length) {
      return;
    }

    const payload = this.selectedBeds.reduce(
      (prev: any, s: { room: any; rates: any }) => {
        let privateRooms = prev.privateRooms;
        let dormRooms = prev.dormRooms;
        let familyRooms = prev.familyRooms;
        const room = {
          privateRoomCategory: s.room.privateRoomCategory,
          roomType: s.room.roomType,
          name: s.room.name,
          _id: s.room._id,
        };

        // rateType: {
        //   standard: 1,
        //   nonRefundable: 2,
        // },

        if (s.room.roomType === 1) {
          s.rates.forEach((rate) => {
            for (let i = 0; i < rate.count; i++) {
              privateRooms.push({
                ...room,
                rateType: rate.rateType === 'standard' ? 1 : 2,
              });
            }
          });
        } else if (s.room.roomType === 2) {
          s.rates.forEach((rate) => {
            dormRooms.push({
              ...room,
              rateType: rate.rateType === 'standard' ? 1 : 2,
              noOfBookedBeds: rate.count,
            });
          });
        } else if (s.room.roomType === 3) {
          s.rates.forEach((rate) => {
            for (let i = 0; i < rate.count; i++) {
              familyRooms.push({
                ...s.room,
                rateType: rate.rateType === 'standard' ? 1 : 2,
              });
            }
          });
        }

        return {
          dormRooms,
          familyRooms,
          privateRooms,
        };
      },
      {
        dormRooms: [],
        familyRooms: [],
        privateRooms: [],
      }
    );

    this.bookingService.updateRooms(payload);

    this.searchService.searchParams$
      .pipe(
        take(1),
        switchMap((searchParams: HostelSearchParams) => {
          return this.apiService
            .lockBooking((this.hostel as any)._id, {
              checkInDate: searchParams.checkInDate,
              checkOutDate: searchParams.checkOutDate,
              noOfGuests: searchParams.noOfGuests,
              // ...beds
              rooms: payload,
            })
            .pipe(
              tap((resp) => {
                this.bookingService.booking = resp.data;
                localStorage.setItem(`booking`, JSON.stringify(resp.data));
                this.router.navigate(['booking', resp.data._id]);
              })
            );
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
